/**
 * Cookie utilities for consistent cookie management
 */

/**
 * Get cookie domain with leading dot for cross-subdomain sharing
 * Returns the full hostname with leading dot (e.g., '.lastmile-test.automapa.pl' from 'lastmile-test.automapa.pl')
 * This allows the cookie to be shared across all subdomains of the current domain
 */
export function getCookieDomain(): string {
    const hostname = window.location.hostname;

    // Don't use domain for localhost or IP addresses
    if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
        return '';
    }

    // Return the full hostname with leading dot
    // e.g., 'lastmile-test.automapa.pl' becomes '.lastmile-test.automapa.pl'
    // This allows sharing cookies across subdomains like 'app.lastmile-test.automapa.pl'
    return `.${hostname}`;
}

/**
 * Set a cookie with consistent domain handling
 */
export function setCookie(
    name: string,
    value: string,
    options: {
        maxAge?: number;
        path?: string;
        sameSite?: 'Strict' | 'Lax' | 'None';
        secure?: boolean;
    } = {}
): void {
    let {
        maxAge = 31536000, // 1 year default
        path = '/',
        sameSite = 'Lax',
        secure = true,
    } = options;
    if (secure && window.location.protocol == 'http:') {
        secure = false;
    }

    const domain = getCookieDomain();
    const domainPart = domain ? `; domain=${domain}` : '';

    document.cookie = `${name}=${value}; path=${path}; max-age=${maxAge}; SameSite=${sameSite}${secure ? '; Secure' : ''}${domainPart}`;
}

/**
 * Get a cookie value by name
 */
export function getCookie(name: string): string | null {
    const nameEQ = name + '=';
    const cookies = document.cookie.split(';');

    for (let i = 0; i < cookies.length; i++) {
        let cookie = cookies[i].trim();
        if (cookie.indexOf(nameEQ) === 0) {
            return cookie.substring(nameEQ.length);
        }
    }

    return null;
}

/**
 * Delete a cookie
 */
export function deleteCookie(name: string): void {
    const domain = getCookieDomain();
    const domainPart = domain ? `; domain=${domain}` : '';

    document.cookie = `${name}=; path=/; max-age=0${domainPart}`;
}
